import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { merchantApi } from '../api/config';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faSearch, faTicket, faCircleDot, faHourglassHalf, faClock, faCheckCircle, faLock,
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import RichTextEditor from '../components/shared/RichTextEditor';
import OrderMultiSelect, { OrderRef } from '../components/shared/OrderMultiSelect';
import TicketAttachmentUploader, { AttachmentItem } from '../components/shared/TicketAttachmentUploader';
import DataTable, { Column } from '../components/shared/DataTable';
import {
  UiPageHeader, UiFilterPanel, UiButton, UiModal, UiInput, UiSelect, StatusBadge, FIELD_LABEL_CLASS,
} from '../components/ui';
import type { StatusDef } from '../components/ui';

type TicketStatus = 'open' | 'in_progress' | 'waiting_merchant' | 'resolved' | 'closed';
type TicketPriority = 'low' | 'normal' | 'high' | 'urgent';
type TicketCategory = 'payin' | 'payout' | 'settlement' | 'other';

interface TicketRow {
  id: string;
  ticket_number: string;
  subject: string;
  category: TicketCategory;
  priority: TicketPriority;
  status: TicketStatus;
  last_reply_at: string | null;
  last_reply_by_type: string | null;
  created_at: string;
  updated_at: string;
}

const STATUS_LABEL: Record<TicketStatus, string> = {
  open: 'เปิด',
  in_progress: 'กำลังดำเนินการ',
  waiting_merchant: 'รอ Merchant ตอบ',
  resolved: 'แก้ไขแล้ว',
  closed: 'ปิด',
};

const STATUS_MAP: Record<string, StatusDef> = {
  open:             { bg: 'bg-blue-100', text: 'text-blue-700', icon: faCircleDot, label: STATUS_LABEL.open },
  in_progress:      { bg: 'bg-amber-100', text: 'text-amber-700', icon: faHourglassHalf, label: STATUS_LABEL.in_progress },
  waiting_merchant: { bg: 'bg-purple-100', text: 'text-purple-700', icon: faClock, label: STATUS_LABEL.waiting_merchant },
  resolved:         { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: faCheckCircle, label: STATUS_LABEL.resolved },
  closed:           { bg: 'bg-gray-100', text: 'text-gray-600', icon: faLock, label: STATUS_LABEL.closed },
};

const PRIORITY_LABEL: Record<TicketPriority, string> = {
  low: 'ต่ำ', normal: 'ปกติ', high: 'สูง', urgent: 'ด่วน',
};
const PRIORITY_COLOR: Record<TicketPriority, string> = {
  low: 'text-slate-500 dark:text-slate-400', normal: 'text-slate-700 dark:text-slate-200',
  high: 'text-orange-600 dark:text-orange-400 font-semibold', urgent: 'text-rose-600 dark:text-rose-400 font-bold',
};
const CATEGORY_LABEL: Record<TicketCategory, string> = {
  payin: 'Payin', payout: 'Payout', settlement: 'Settlement', other: 'อื่นๆ',
};

function fmtDate(s?: string | null) {
  if (!s) return '-';
  const d = new Date(s);
  return d.toLocaleString('th-TH', { dateStyle: 'short', timeStyle: 'short' });
}

const TicketsPage: React.FC = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<TicketRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<TicketStatus | ''>('');
  const [filterPriority, setFilterPriority] = useState<TicketPriority | ''>('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [appliedStartDate, setAppliedStartDate] = useState('');
  const [appliedEndDate, setAppliedEndDate] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const limit = 20;
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    merchantApi
      .get('/tickets', {
        params: {
          status: filterStatus || undefined,
          priority: filterPriority || undefined,
          search: debouncedSearch || undefined,
          start_date: appliedStartDate || undefined,
          end_date: appliedEndDate || undefined,
          limit, offset,
        },
      })
      .then(res => {
        if (cancelled) return;
        setTickets(res.data?.data || []);
        setTotal(res.data?.total || 0);
      })
      .catch(() => {
        if (!cancelled) toast.error('โหลด tickets ไม่สำเร็จ');
      })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [filterStatus, filterPriority, debouncedSearch, appliedStartDate, appliedEndDate, offset]);

  const refreshList = () => {
    setOffset(0);
    setShowCreate(false);
    setLoading(true);
    merchantApi.get('/tickets', { params: { limit, offset: 0 } })
      .then(res => {
        setTickets(res.data?.data || []);
        setTotal(res.data?.total || 0);
      })
      .finally(() => setLoading(false));
  };

  const handleSearch = () => {
    if (startDate && endDate && startDate > endDate) {
      toast.error('วันที่เริ่มต้นต้องน้อยกว่าหรือเท่ากับวันที่สิ้นสุด');
      return;
    }
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setOffset(0);
  };

  const handleReset = () => {
    setStartDate('');
    setEndDate('');
    setAppliedStartDate('');
    setAppliedEndDate('');
    setFilterStatus('');
    setFilterPriority('');
    setSearch('');
    setOffset(0);
  };

  const columns: Column<TicketRow>[] = [
    {
      key: 'ticket_number',
      header: 'Ticket #',
      render: (t) => <span className="font-mono text-xs text-gray-700 dark:text-slate-300">{t.ticket_number}</span>,
    },
    {
      key: 'subject',
      header: 'หัวข้อ',
      render: (t) => <span className="text-gray-900 dark:text-slate-100 font-medium truncate block max-w-[280px]">{t.subject}</span>,
    },
    {
      key: 'category',
      header: 'หมวด',
      render: (t) => <span className="text-xs text-gray-600 dark:text-slate-400">{CATEGORY_LABEL[t.category]}</span>,
    },
    {
      key: 'priority',
      header: 'Priority',
      render: (t) => <span className={`text-xs ${PRIORITY_COLOR[t.priority]}`}>{PRIORITY_LABEL[t.priority]}</span>,
    },
    {
      key: 'status',
      header: 'สถานะ',
      render: (t) => <StatusBadge status={t.status} statusMap={STATUS_MAP} />,
    },
    {
      key: 'updated',
      header: 'อัปเดตล่าสุด',
      render: (t) => <span className="text-xs text-gray-500 dark:text-slate-400">{fmtDate(t.last_reply_at || t.created_at)}</span>,
    },
  ];

  return (
    <div className="w-full">
      <UiPageHeader
        icon={faTicket}
        title="Support Tickets"
        subtitle="เปิด ticket ติดต่อทีมงานเพื่อขอความช่วยเหลือ"
        actions={
          <UiButton icon={faPlus} onClick={() => setShowCreate(true)}>
            เปิด Ticket ใหม่
          </UiButton>
        }
      />

      <UiFilterPanel
        dateFrom={startDate}
        dateTo={endDate}
        onDateFromChange={setStartDate}
        onDateToChange={setEndDate}
        onSearch={handleSearch}
        onReset={handleReset}
        loading={loading}
      >
        <UiInput
          wrapperClassName="flex-1 min-w-[200px]"
          label="ค้นหา"
          icon={faSearch}
          value={search}
          onChange={e => { setSearch(e.target.value); setOffset(0); }}
          placeholder="ค้นหา ticket number / หัวข้อ..."
        />
        <UiSelect
          wrapperClassName="w-fit min-w-[150px]"
          label="สถานะ"
          value={filterStatus}
          onChange={e => { setFilterStatus(e.target.value as any); setOffset(0); }}
        >
          <option value="">ทุกสถานะ</option>
          {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </UiSelect>
        <UiSelect
          wrapperClassName="w-fit min-w-[130px]"
          label="Priority"
          value={filterPriority}
          onChange={e => { setFilterPriority(e.target.value as any); setOffset(0); }}
        >
          <option value="">ทุก priority</option>
          {Object.entries(PRIORITY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </UiSelect>
      </UiFilterPanel>

      <DataTable<TicketRow>
        data={tickets}
        columns={columns}
        loading={loading}
        emptyMessage="ยังไม่มี ticket"
        emptyIcon={<FontAwesomeIcon icon={faTicket} className="text-5xl text-gray-300 dark:text-slate-600" />}
        keyExtractor={(t) => t.id}
        onRowClick={(t) => navigate(`/tickets/${t.id}`)}
        serverPagination={{
          currentPage: Math.floor(offset / limit) + 1,
          totalItems: total,
          pageSize: limit,
          pageSizeOptions: [limit],
          onPageChange: (p) => setOffset((p - 1) * limit),
        }}
        hoverable
      />

      <CreateTicketModal open={showCreate} onClose={() => setShowCreate(false)} onCreated={refreshList} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Create Ticket Modal
// ---------------------------------------------------------------------------
interface CreateModalProps { open: boolean; onClose: () => void; onCreated: () => void; }
const CreateTicketModal: React.FC<CreateModalProps> = ({ open, onClose, onCreated }) => {
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<TicketCategory>('other');
  const [priority, setPriority] = useState<TicketPriority>('normal');
  const [related, setRelated] = useState<OrderRef[]>([]);
  const [attachments, setAttachments] = useState<AttachmentItem[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = useMemo(() => {
    return subject.trim().length > 0 && description.replace(/<[^>]+>/g, '').trim().length > 0;
  }, [subject, description]);

  async function submit() {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await merchantApi.post('/tickets', {
        subject: subject.trim(),
        description_html: description,
        category,
        priority,
        related_orders: related.map(r => ({ ref_type: r.ref_type, ref_id: r.ref_id })),
        attachments: attachments.map(a => ({ file_url: a.file_url, file_name: a.file_name, mime_type: a.mime_type, size_bytes: a.size_bytes })),
      });
      if (res.data?.success) {
        toast.success('เปิด ticket สำเร็จ');
        onCreated();
      } else {
        toast.error(res.data?.message || 'เกิดข้อผิดพลาด');
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'ไม่สามารถสร้าง ticket ได้');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <UiModal
      open={open}
      onClose={onClose}
      title="เปิด Ticket ใหม่"
      icon={faTicket}
      iconStyle={{ color: 'var(--theme-primary)' }}
      size="xl"
      scrollable
      footer={
        <div className="flex justify-end gap-2">
          <UiButton variant="secondary" onClick={onClose}>ยกเลิก</UiButton>
          <UiButton onClick={submit} loading={submitting} disabled={!canSubmit}>
            ส่ง Ticket
          </UiButton>
        </div>
      }
    >
      <UiInput
        label="หัวข้อ *"
        value={subject}
        onChange={e => setSubject(e.target.value)}
        maxLength={200}
        placeholder="สรุปปัญหาเป็นประโยคสั้นๆ"
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <UiSelect label="หมวด" value={category} onChange={e => setCategory(e.target.value as TicketCategory)}>
          {Object.entries(CATEGORY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </UiSelect>
        <UiSelect label="Priority" value={priority} onChange={e => setPriority(e.target.value as TicketPriority)}>
          {Object.entries(PRIORITY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </UiSelect>
      </div>

      <div>
        <label className={FIELD_LABEL_CLASS}>รายละเอียด *</label>
        <RichTextEditor value={description} onChange={setDescription} placeholder="อธิบายปัญหา ขั้นตอนที่ทำ ผลที่ได้ ฯลฯ" minHeight={180} />
      </div>

      <div>
        <label className={FIELD_LABEL_CLASS}>Order ที่เกี่ยวข้อง (เลือกได้หลายชนิด)</label>
        <OrderMultiSelect value={related} onChange={setRelated} />
      </div>

      <div>
        <label className={FIELD_LABEL_CLASS}>ไฟล์แนบ</label>
        <TicketAttachmentUploader value={attachments} onChange={setAttachments} />
      </div>
    </UiModal>
  );
};

export default TicketsPage;
